import commaNumber from 'comma-number';
import { ArrowRight, Info } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/shadcn/card';
import { FlowerSpinner } from '@/shared/components/loader/flower-spinner';
import { Table } from '@/shared/components/shadcn/table';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { cn } from '@/shared/utils';

export function SummaryStatCard({
  helper,
  infoTooltip,
  label,
  value,
}: {
  helper?: string;
  infoTooltip?: string;
  label: string;
  value: string;
}) {
  return (
    <Card className="border-border bg-card gap-0 border">
      <CardHeader className="mb-1.5 gap-0 pb-0">
        <CardTitle className="text-base-muted-foreground flex items-center gap-1.5 text-sm font-normal">
          <span>{label}</span>
          {infoTooltip ? (
            <TooltipFlat asChild side="top" contentClassName="max-w-56" content={infoTooltip}>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label={`${label} calculation info`}
              >
                <Info className="size-3.5" />
              </button>
            </TooltipFlat>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col pt-0">
        <div className="text-base-card-foreground text-2xl font-bold">{value}</div>
        {helper ? (
          <p className="text-base-card-foreground mt-3 text-sm font-normal">{helper}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function OverviewMetricSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-foreground text-xl leading-6 font-semibold">{title}</h2>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">{children}</div>
    </section>
  );
}

export function OverviewShortcutCard({
  actionLabel,
  description,
  infoTooltip,
  label,
  onAction,
  showDescription = false,
  value,
}: {
  actionLabel: string;
  description: ReactNode;
  infoTooltip?: string;
  label: string;
  onAction: () => void;
  showDescription?: boolean;
  value: string;
}) {
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onAction}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onAction();
        }
      }}
      className="hover:bg-muted focus-visible:ring-ring h-full cursor-pointer gap-0 transition-colors focus-visible:ring-2 focus-visible:outline-none"
    >
      <CardHeader className="pb-4">
        <CardTitle className="text-base-muted-foreground flex items-center gap-1.5 text-sm font-normal">
          <span>{label}</span>
          {infoTooltip ? (
            <TooltipFlat asChild side="top" contentClassName="max-w-56" content={infoTooltip}>
              <span
                className="text-muted-foreground hover:text-foreground inline-flex cursor-pointer transition-colors"
                aria-hidden="true"
              >
                <Info className="size-3.5" />
              </span>
            </TooltipFlat>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <div className="text-base-card-foreground text-3xl font-bold tracking-tight">{value}</div>
        {showDescription ? (
          <CardDescription className="text-sm leading-6">{description}</CardDescription>
        ) : null}
      </CardContent>
      <CardFooter className="pt-4">
        <div className="text-muted-foreground inline-flex items-center gap-1 text-sm font-medium">
          {actionLabel}
          <ArrowRight className="size-4" />
        </div>
      </CardFooter>
    </Card>
  );
}

export function FilterPills({
  onChange,
  options,
  value,
}: {
  onChange: (value: string) => void;
  options: Array<{
    count?: number | string;
    isCountLoading?: boolean;
    label: string;
    tooltip?: string;
    value: string;
  }>;
  value: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            'inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
            value === option.value
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-card text-muted-foreground hover:border-foreground/20 hover:text-foreground',
          )}
        >
          <span className="inline-flex items-center gap-1.5">
            <span>{option.label}</span>
            {option.tooltip ? (
              <TooltipFlat asChild side="top" contentClassName="max-w-56" content={option.tooltip}>
                <span
                  className="inline-flex cursor-pointer opacity-70 transition-opacity hover:opacity-100"
                  aria-hidden="true"
                >
                  <Info className="size-3.5" />
                </span>
              </TooltipFlat>
            ) : null}
          </span>
          {option.isCountLoading || typeof option.count === 'number' ? (
            <span
              className={cn(
                'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
                value === option.value
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {option.isCountLoading ? (
                <FlowerSpinner className="size-3" />
              ) : typeof option.count === 'number' ? (
                commaNumber(option.count)
              ) : null}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export function ViewModeToggle({
  onChange,
  options,
  value,
}: {
  onChange: (value: string) => void;
  options: Array<{ label: string; value: string }>;
  value: string;
}) {
  return (
    <Tabs value={value} onValueChange={onChange} className="gap-0">
      <TabsList>
        {options.map((option) => (
          <TabsTrigger key={option.value} value={option.value}>
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export function CampaignDataTable({
  children,
  footer,
}: {
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border">
      <Table>{children}</Table>
      {footer ? <div className="border-border border-t px-4 py-3">{footer}</div> : null}
    </div>
  );
}

export function EmptyInlineState({
  action,
  description,
  title,
}: {
  action?: ReactNode;
  description: string;
  title: string;
}) {
  return (
    <div className="bg-muted border-border rounded-xl border border-dashed px-6 py-10 text-center">
      <p className="text-foreground text-base font-medium">{title}</p>
      <p className="text-muted-foreground mt-2 text-sm leading-6">{description}</p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}
